import {buildChannel} from "@hosttools/frontend/models/channel";
import type {Listing} from "@hosttools/frontend/models/listing";
import type {Lock} from "@hosttools/frontend/models/lock";
import sortBy from "lodash/sortBy";
import {forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState} from "react";
import type React from "react";
import type {ForwardRefRenderFunction} from "react";

import LockListingSelect from "@/client/components/LockListingSelect";

type LockOption = {
    value: string;
    label: string;
    source: "August" | "Seam";
    icon: string;
    Icon?: React.ReactElement;
};

export type Props = {
    listing: Listing;
    locks: Lock[];
    onChange: (linkedLocks: Lock[]) => void;
    onAdd?: (lock: Lock) => void;
    onRemove?: (lock: Lock) => void;
    disabled?: boolean;
};

export interface SmartLockRef {
    addLocks: (locks: Lock[]) => void;
    removeLocks: (locks: Lock[]) => void;
}

const SmartLock: ForwardRefRenderFunction<SmartLockRef, Props> = (
    {listing, locks, onChange, onAdd, onRemove, disabled},
    ref
) => {
    const [linkedLocks, setLinkedLocks] = useState<Lock[]>([]);
    const [lockOptions, setLockOptions] = useState<LockOption[]>([]);

    useImperativeHandle(
        ref,
        () => ({
            addLocks: addedLocks => {
                setLinkedLocks(prev => {
                    const next = addedLocks.reduce(
                        (arr, lock) => {
                            const found = prev.find(elem => elem._id === lock._id);
                            if (!found) {
                                arr.push(lock);
                            }
                            return arr;
                        },
                        [...prev]
                    );
                    return next;
                });
            },
            removeLocks: removingLocks => {
                const ids = removingLocks.map(elem => elem._id);
                setLinkedLocks(prev => prev.filter(elem => !ids.includes(elem._id)));
            }
        }),
        []
    );

    const buildLockOptions = useCallback(
        (newLinkedLocks: Lock[]) => {
            const linkedLockIDs = newLinkedLocks.map(lock => lock._id);
            let options = locks.reduce<LockOption[]>((result, lock) => {
                const isLinkedLock = linkedLockIDs.find(linkedLockID => {
                    return linkedLockID === lock._id;
                });
                if (isLinkedLock) {
                    return result;
                }
                const channel = buildChannel(lock.source);

                result.push({
                    value: lock._id,
                    label: lock.name,
                    source: lock.source,
                    icon: channel.logo,
                    Icon: <channel.LogoIcon />
                });
                return result;
            }, []);

            options = sortBy(options, option => option.label.toLowerCase());

            setLockOptions(options);
        },
        [locks]
    );

    const deleteLock = useCallback(
        (lockToRemove: Lock) => {
            const filteredLocks = linkedLocks.filter(lock => {
                return lock._id !== lockToRemove._id;
            });
            buildLockOptions(filteredLocks);
            setLinkedLocks(filteredLocks);
            onChange(filteredLocks);
            onRemove?.(lockToRemove);
        },
        [buildLockOptions, linkedLocks, onChange, onRemove]
    );

    const handleSelectedOption = useCallback(
        (option: LockOption | null) => {
            const newLock =
                option &&
                locks.find(lock => {
                    return option.value === lock._id;
                });
            if (!newLock) {
                return;
            }
            onAdd?.(newLock);
            setLinkedLocks(prev => {
                // Make sure the listing isn't already on the linkedListings list
                if (prev.some(linkedLock => newLock._id === linkedLock._id)) {
                    return prev;
                }
                const next = [...prev, newLock];
                return next;
            });
        },
        [locks, onAdd]
    );

    useEffect(() => {
        buildLockOptions(linkedLocks);
        onChange(linkedLocks);
    }, [linkedLocks, buildLockOptions, onChange]);

    useEffect(() => {
        const listingID = listing._id;
        const linkedLocks = locks.reduce<Lock[]>((result, lock) => {
            if (lock.listingIDs?.includes(listingID)) {
                result.push(lock);
            }
            return result;
        }, []);
        setLinkedLocks(linkedLocks);
    }, [listing, locks]);

    return (
        <LockListingSelect
            disabled={disabled}
            options={lockOptions}
            selectedItems={linkedLocks}
            onRemoveItem={deleteLock}
            onSelectOption={handleSelectedOption}
        />
    );
};

export default memo(forwardRef(SmartLock));
